import type { ThemeDefinition } from './types';

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) =>
        Math.max(0, Math.min(255, Math.round(x)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/**
 * Auto-derive a dark variant from a light theme definition.
 * Darkens backgrounds, lightens text, and tints surfaces with the primary hue.
 */
export function deriveDarkVariant(theme: ThemeDefinition): ThemeDefinition {
  const [pr, pg, pb] = hexToRgb(theme.tokens.colorPrimary);
  const tint = 0.06;

  const darkBgBase = rgbToHex(12 + pr * tint, 14 + pg * tint, 20 + pb * tint);
  const darkBgContainer = rgbToHex(22 + pr * tint, 26 + pg * tint, 32 + pb * tint);
  const darkBgElevated = rgbToHex(30 + pr * tint, 35 + pg * tint, 42 + pb * tint);
  const darkBorder = rgbToHex(42 + pr * tint, 48 + pg * tint, 58 + pb * tint);
  const darkBorderSecondary = rgbToHex(55 + pr * tint, 62 + pg * tint, 72 + pb * tint);

  return {
    ...theme,
    id: `${theme.id}-dark`,
    name: `${theme.name} (Dark)`,
    mode: 'dark',
    tokens: {
      ...theme.tokens,
      colorBgBase: darkBgBase,
      colorBgContainer: darkBgContainer,
      colorBgElevated: darkBgElevated,
      colorBgSidebar: darkBgBase,
      colorText: '#F1F0EE',
      colorTextSecondary: '#A0A7B0',
      colorTextTertiary: '#6B7280',
      colorBorder: darkBorder,
      colorBorderSecondary: darkBorderSecondary,
      boxShadow: '0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)',
      boxShadowSecondary: '0 4px 16px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.25)',
      glassOpacity: '0.06',
      glassSaturation: '180%',
      cardBorder: darkBorder,
      cardShadow: '0 2px 8px rgba(0,0,0,0.25)',
    },
  };
}
